import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
    ArrowLeft, ArrowRight, BadgeCheck, Building2, ExternalLink, Flame, Leaf, Loader2,
    MapPin, Sparkles, Star, Users, Zap, ShieldCheck,
} from "lucide-react";
import SupplierAvatar from "@/components/SupplierAvatar";
import { useSiteContent } from "@/lib/useSiteContent";
import { keysForMany } from "@/config/cmsSections";

function Stars({ value, size = 16 }) {
    const v = Number(value);
    return (
        <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((i) => (
                <Star
                    key={i}
                    style={{ width: size, height: size }}
                    className={i <= Math.round(v) ? "fill-accent text-accent" : "text-muted-foreground/30"}
                />
            ))}
        </div>
    );
}

function MiniBar({ label, value }) {
    const width = `${(Number(value) / 5) * 100}%`;
    return (
        <div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-mono font-bold">{Number(value).toFixed(1)}</span>
            </div>
            <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                <div className="h-full bg-primary rounded-full" style={{ width }} />
            </div>
        </div>
    );
}

const AudienceIcon = { b2c: Users, b2b: Building2, both: BadgeCheck };

export default function SupplierDetail() {
    const { slug } = useParams();
    const c = useSiteContent(keysForMany("common", "supplier-detail"));
    const AudienceLabel = {
        b2c: c("common.audience.b2c_plural", "Particuliers"),
        b2b: c("common.audience.b2b_plural", "Professionnels"),
        both: c("common.audience.both", "Particuliers · Pros"),
    };
    const [supplier, setSupplier] = useState(null);
    const [offers, setOffers] = useState([]);
    const [reviewsData, setReviewsData] = useState({ count: 0, average_rating: 0, distribution: {}, reviews: [] });
    const [loading, setLoading] = useState(true);

    const [aiLoading, setAiLoading] = useState(false);
    const [analysis, setAnalysis] = useState(null);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const [s, o, r] = await Promise.all([
                    api.get(`/suppliers/${slug}`),
                    api.get(`/suppliers/${slug}/offers`),
                    api.get(`/suppliers/${slug}/reviews`),
                ]);
                setSupplier(s.data);
                setOffers(o.data);
                setReviewsData(r.data);
            } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [slug]);

    const runAnalysis = async () => {
        setAiLoading(true);
        try {
            const { data } = await api.post(`/suppliers/${slug}/ai-analysis`);
            setAnalysis(data.analysis);
            toast.success("Analyse générée");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setAiLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="em-container py-20 text-center text-muted-foreground" data-testid="supplier-loading">
                <Loader2 className="h-8 w-8 mx-auto animate-spin" />
                <p className="mt-3">Chargement du fournisseur…</p>
            </div>
        );
    }

    if (!supplier) {
        return (
            <div className="em-container py-20" data-testid="supplier-not-found">
                <h1 className="font-display text-4xl font-black text-primary">{c("supplier_detail.notfound_title", "Fournisseur introuvable")}</h1>
                <Button asChild className="rounded-full mt-6">
                    <Link to="/fournisseurs"><ArrowLeft className="h-4 w-4 mr-2" />{c("supplier_detail.notfound_cta", "Retour à la liste")}</Link>
                </Button>
            </div>
        );
    }

    const Icon = AudienceIcon[supplier.target_audience] || BadgeCheck;
    const elecOffers = offers.filter((o) => o.energy_type === "electricity");
    const gasOffers = offers.filter((o) => o.energy_type === "gas");

    return (
        <div className="em-container py-12" data-testid="supplier-detail-page">
            <Button asChild variant="ghost" size="sm" className="rounded-full mb-6" data-testid="back-to-suppliers">
                <Link to="/fournisseurs"><ArrowLeft className="h-4 w-4 mr-2" />{c("supplier_detail.back_label", "Tous les fournisseurs")}</Link>
            </Button>

            {/* HERO */}
            <Card className="rounded-3xl border-border overflow-hidden" data-testid="supplier-hero">
                <CardContent className="p-7 md:p-10">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
                        <div className="lg:col-span-2">
                            <div className="flex items-center gap-4">
                                <SupplierAvatar slug={supplier.slug} name={supplier.name} size="lg" />
                                <div>
                                    <p className="em-eyebrow">{c("supplier_detail.hero.eyebrow", "Fournisseur d'énergie")}</p>
                                    <h1 className="font-display text-4xl md:text-5xl font-black text-primary tracking-tight">{supplier.name}</h1>
                                </div>
                            </div>
                            <p className="text-lg italic text-muted-foreground mt-5">"{supplier.tagline}"</p>
                            <p className="text-base leading-relaxed mt-3">{supplier.description}</p>

                            <div className="flex flex-wrap gap-2 mt-5" data-testid="supplier-tags">
                                <Badge variant="outline" className="rounded-full"><MapPin className="h-3 w-3 mr-1" />{supplier.headquarters}</Badge>
                                <Badge variant="outline" className="rounded-full">{c("supplier_detail.hero.founded_prefix", "Fondé en")} {supplier.year_founded}</Badge>
                                <Badge variant="outline" className="rounded-full"><Icon className="h-3 w-3 mr-1" />{AudienceLabel[supplier.target_audience]}</Badge>
                                {supplier.electricity && (
                                    <Badge variant="outline" className="rounded-full"><Zap className="h-3 w-3 mr-1" />{c("supplier_detail.hero.badge_elec", "Électricité")}</Badge>
                                )}
                                {supplier.gas && (
                                    <Badge variant="outline" className="rounded-full"><Flame className="h-3 w-3 mr-1" />{c("supplier_detail.hero.badge_gas", "Gaz")}</Badge>
                                )}
                                {supplier.green_energy && (
                                    <Badge className="rounded-full bg-primary text-primary-foreground hover:bg-primary"><Leaf className="h-3 w-3 mr-1" />{c("supplier_detail.hero.badge_green", "100% verte")}</Badge>
                                )}
                            </div>

                            <div className="flex flex-wrap gap-3 mt-7">
                                <Button asChild size="lg" className="rounded-full bg-primary hover:bg-primary/90" data-testid="hero-compare-btn">
                                    <Link to="/comparateur">{c("supplier_detail.hero.compare_cta", "Comparer ses offres")} <ArrowRight className="h-4 w-4 ml-2" /></Link>
                                </Button>
                                <Button asChild size="lg" variant="outline" className="rounded-full" data-testid="hero-quote-btn">
                                    <Link to="/devis">{c("supplier_detail.hero.quote_cta", "Demander un devis")}</Link>
                                </Button>
                                {supplier.website && (
                                    <Button asChild size="lg" variant="ghost" className="rounded-full" data-testid="hero-website-btn">
                                        <a href={supplier.website} target="_blank" rel="noopener noreferrer">
                                            {c("supplier_detail.hero.website_cta", "Site officiel")} <ExternalLink className="h-4 w-4 ml-2" />
                                        </a>
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* RIGHT: Big rating */}
                        <div className="bg-primary text-primary-foreground rounded-3xl p-7" data-testid="supplier-overall">
                            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-accent">{c("supplier_detail.hero.overall_label", "Note globale")}</p>
                            <p className="font-display text-7xl font-black mt-2 leading-none">
                                {Number(supplier.overall_rating).toFixed(1)}
                                <span className="text-3xl text-primary-foreground/60">/5</span>
                            </p>
                            <div className="mt-2"><Stars value={supplier.overall_rating} size={20} /></div>
                            <div className="mt-7 space-y-3">
                                <MiniBarDark label={c("supplier_detail.hero.bar_transparency", "Transparence")} value={supplier.transparency_rating} />
                                <MiniBarDark label={c("supplier_detail.hero.bar_service", "Service client")} value={supplier.customer_service_rating} />
                                <MiniBarDark label={c("supplier_detail.hero.bar_green", "Note verte")} value={supplier.green_rating} />
                                <MiniBarDark label={c("supplier_detail.hero.bar_price", "Prix")} value={supplier.price_rating} />
                                <MiniBarDark label={c("supplier_detail.hero.bar_digital", "Digital")} value={supplier.digital_rating} />
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* TABS */}
            <Tabs defaultValue="offers" className="mt-10" data-testid="supplier-tabs">
                <TabsList className="rounded-full">
                    <TabsTrigger value="offers" className="rounded-full" data-testid="tab-offers">{c("supplier_detail.tabs.offers", "Offres")} ({offers.length})</TabsTrigger>
                    <TabsTrigger value="reviews" className="rounded-full" data-testid="tab-reviews">{c("supplier_detail.tabs.reviews", "Avis")} ({reviewsData.count})</TabsTrigger>
                    <TabsTrigger value="profile" className="rounded-full" data-testid="tab-profile">{c("supplier_detail.tabs.profile", "Profil détaillé")}</TabsTrigger>
                    <TabsTrigger value="ai" className="rounded-full" data-testid="tab-ai">{c("supplier_detail.tabs.ai", "Analyse")}</TabsTrigger>
                </TabsList>

                {/* OFFRES */}
                <TabsContent value="offers" className="mt-6">
                    {offers.length === 0 ? (
                        <Card className="rounded-3xl border-border" data-testid="offers-empty">
                            <CardContent className="p-10 text-center text-muted-foreground">
                                {c("supplier_detail.offers.empty", "Aucune offre publique référencée pour ce fournisseur.")}
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="space-y-8">
                            {elecOffers.length > 0 && (
                                <OffersGroup
                                    title={c("supplier_detail.offers.elec_title", "Offres électricité")}
                                    icon={<Zap className="h-5 w-5 text-primary" />}
                                    items={elecOffers}
                                    fixedBadge={c("supplier_detail.offers.fixed_badge", "Prix fixe")}
                                    b2bLabel={c("common.audience.b2b_plural", "Pros")}
                                    b2cLabel={c("common.audience.b2c_plural", "Particuliers")}
                                    priceLabel={c("supplier_detail.offers.price_label", "Prix kWh")}
                                    subLabel={c("supplier_detail.offers.sub_label", "Abonnement")}
                                />
                            )}
                            {gasOffers.length > 0 && (
                                <OffersGroup
                                    title={c("supplier_detail.offers.gas_title", "Offres gaz")}
                                    icon={<Flame className="h-5 w-5 text-primary" />}
                                    items={gasOffers}
                                    fixedBadge={c("supplier_detail.offers.fixed_badge", "Prix fixe")}
                                    b2bLabel={c("common.audience.b2b_plural", "Pros")}
                                    b2cLabel={c("common.audience.b2c_plural", "Particuliers")}
                                    priceLabel={c("supplier_detail.offers.price_label", "Prix kWh")}
                                    subLabel={c("supplier_detail.offers.sub_label", "Abonnement")}
                                />
                            )}
                        </div>
                    )}
                </TabsContent>

                {/* AVIS */}
                <TabsContent value="reviews" className="mt-6">
                    <ReviewsBlock
                        data={reviewsData}
                        emptyText={c("supplier_detail.reviews.empty", "Aucun avis pour l'instant. Soyez le premier à partager votre expérience.")}
                        overallLabel={c("supplier_detail.reviews.overall_label", "Note globale")}
                        countSuffix={c("supplier_detail.reviews.count_suffix", "avis client")}
                        verifiedSuffix={c("supplier_detail.reviews.verified_suffix", "Vérifié")}
                    />
                </TabsContent>

                {/* PROFIL */}
                <TabsContent value="profile" className="mt-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="rounded-3xl border-border">
                            <CardContent className="p-7">
                                <p className="em-eyebrow mb-3">{c("supplier_detail.profile.services_label", "Services proposés")}</p>
                                <ul className="space-y-2">
                                    {(supplier.services || []).map((sv) => (
                                        <li key={sv} className="flex gap-2 text-sm"><BadgeCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" />{sv}</li>
                                    ))}
                                </ul>
                            </CardContent>
                        </Card>
                        <Card className="rounded-3xl border-border">
                            <CardContent className="p-7 space-y-5">
                                <div>
                                    <p className="em-eyebrow mb-2">{c("supplier_detail.profile.pros_label", "Atouts")}</p>
                                    <ul className="space-y-1.5">
                                        {supplier.pros.map((p) => (
                                            <li key={p} className="text-sm flex gap-2"><ShieldCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" />{p}</li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <p className="em-eyebrow mb-2">{c("supplier_detail.profile.cons_label", "Limites")}</p>
                                    <ul className="space-y-1.5">
                                        {supplier.cons.map((con) => (
                                            <li key={con} className="text-sm text-muted-foreground flex gap-2">— {con}</li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="pt-4 border-t border-border">
                                    <p className="em-eyebrow mb-1">{c("supplier_detail.profile.best_for_label", "Idéal pour")}</p>
                                    <p className="text-sm">{supplier.best_for}</p>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* AI */}
                <TabsContent value="ai" className="mt-6">
                    <Card className="rounded-3xl bg-primary text-primary-foreground border-0">
                        <CardContent className="p-7 md:p-10">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <p className="text-xs tracking-[0.2em] uppercase text-accent">{c("supplier_detail.ai.eyebrow", "Analyse moteur")}</p>
                                    <h3 className="font-display text-3xl font-black mt-2">{c("supplier_detail.ai.title", "Analyse Energy Market")}</h3>
                                    <p className="text-primary-foreground/80 mt-2 max-w-2xl text-sm">
                                        {c("supplier_detail.ai.body", "Notre moteur synthétise les données publiques, avis vérifiés et observations marché pour proposer un verdict argumenté.")}
                                    </p>
                                </div>
                                <Button
                                    onClick={runAnalysis}
                                    disabled={aiLoading}
                                    type="button"
                                    className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold shrink-0"
                                    data-testid="run-ai-analysis"
                                >
                                    {aiLoading ? (
                                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analyse…</>
                                    ) : (
                                        <><Sparkles className="h-4 w-4 mr-2" />{c("supplier_detail.ai.cta", "Générer l'analyse")}</>
                                    )}
                                </Button>
                            </div>
                            {analysis && (
                                <div className="mt-6 bg-primary-foreground/10 rounded-2xl p-6 text-sm whitespace-pre-line leading-relaxed" data-testid="ai-analysis-content">
                                    {analysis}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}

function MiniBarDark({ label, value }) {
    const width = `${(Number(value) / 5) * 100}%`;
    return (
        <div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
                <span className="text-primary-foreground/70">{label}</span>
                <span className="font-mono font-bold">{Number(value).toFixed(1)}</span>
            </div>
            <div className="h-1.5 rounded-full bg-primary-foreground/15 overflow-hidden">
                <div className="h-full bg-accent rounded-full" style={{ width }} />
            </div>
        </div>
    );
}

function OffersGroup({ title, icon, items, fixedBadge = "Prix fixe", b2bLabel = "Pros", b2cLabel = "Particuliers", priceLabel = "Prix kWh", subLabel = "Abonnement" }) {
    return (
        <div data-testid={`offers-group-${title.includes("élec") ? "electricity" : "gas"}`}>
            <div className="flex items-center gap-2 mb-4">
                {icon}
                <h3 className="font-display text-2xl font-bold text-primary">{title}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {items.map((o) => (
                    <Card key={o.id} className="rounded-2xl border-border em-card-hover" data-testid={`offer-${o.id}`}>
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                                <h4 className="font-display font-bold text-lg text-primary">{o.name}</h4>
                                {o.fixed_price && <Badge variant="outline" className="rounded-full">{fixedBadge}</Badge>}
                            </div>
                            <div className="flex flex-wrap gap-1.5 mt-2">
                                <Badge variant="outline" className="rounded-full text-xs">
                                    {o.audience === "b2b" ? b2bLabel : b2cLabel}
                                </Badge>
                                {o.green && (
                                    <Badge className="rounded-full text-xs bg-primary text-primary-foreground hover:bg-primary">
                                        <Leaf className="h-3 w-3 mr-1" />Verte
                                    </Badge>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-4 mt-5 pt-5 border-t border-border">
                                <div>
                                    <p className="em-eyebrow">{priceLabel}</p>
                                    <p className="font-mono font-bold text-2xl text-primary mt-1">{o.price_per_kwh.toFixed(4)} €</p>
                                </div>
                                <div>
                                    <p className="em-eyebrow">{subLabel}</p>
                                    <p className="font-mono font-bold text-2xl text-primary mt-1">{o.subscription_monthly.toFixed(2)} €<span className="text-sm text-muted-foreground">/mois</span></p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
}

function ReviewsBlock({ data, emptyText = "Aucun avis pour l'instant.", overallLabel = "Note globale", countSuffix = "avis client", verifiedSuffix = "Vérifié" }) {
    const { count, average_rating, distribution, reviews } = data;
    if (count === 0) {
        return (
            <Card className="rounded-3xl border-border" data-testid="reviews-empty">
                <CardContent className="p-10 text-center text-muted-foreground">
                    {emptyText}
                </CardContent>
            </Card>
        );
    }
    const total = count;
    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="reviews-block">
            {/* Summary */}
            <Card className="rounded-3xl border-border md:col-span-1 h-fit">
                <CardContent className="p-7">
                    <p className="em-eyebrow">{overallLabel}</p>
                    <p className="font-display text-6xl font-black text-primary mt-2 leading-none">
                        {Number(average_rating).toFixed(1)}<span className="text-2xl text-muted-foreground">/5</span>
                    </p>
                    <div className="mt-2"><Stars value={average_rating} size={20} /></div>
                    <p className="text-sm text-muted-foreground mt-2">{count} {countSuffix}</p>

                    <div className="mt-6 space-y-2">
                        {[5, 4, 3, 2, 1].map((n) => {
                            const c = distribution[String(n)] || 0;
                            const w = total > 0 ? `${(c / total) * 100}%` : "0%";
                            return (
                                <div key={n} className="flex items-center gap-2 text-xs">
                                    <span className="font-mono w-6">{n}★</span>
                                    <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
                                        <div className="h-full bg-accent rounded-full" style={{ width: w }} />
                                    </div>
                                    <span className="font-mono w-6 text-muted-foreground">{c}</span>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* List */}
            <div className="md:col-span-2 space-y-3">
                {reviews.map((r) => (
                    <Card key={r.id} className="rounded-2xl border-border" data-testid={`review-${r.id}`}>
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="font-display font-bold">{r.author}</p>
                                    <p className="text-xs text-muted-foreground">{r.city} · {r.date}{r.verified && ` · ✓ ${verifiedSuffix}`}</p>
                                </div>
                                <Stars value={r.rating} size={14} />
                            </div>
                            <h4 className="font-display font-bold text-primary mt-3">{r.title}</h4>
                            <p className="text-sm text-foreground mt-2 leading-relaxed">{r.text}</p>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
}
